import { useCallback, useEffect, useRef, useState } from 'react';
import { updateUserProfileName } from '@/api/query/customer';

export const useProfileNameEditor = ({ userName, open, onNameUpdated, translations }) => {
	const wasOpenRef = useRef(false);
	const lastSavedNameRef = useRef(userName ?? '');
	const [displayName, setDisplayName] = useState(userName ?? '');
	const [isSavingName, setIsSavingName] = useState(false);
	const [nameError, setNameError] = useState('');

	useEffect(() => {
		const nextName = userName ?? '';
		setDisplayName(nextName);
		lastSavedNameRef.current = nextName;
	}, [userName]);

	const saveDisplayName = useCallback(async () => {
		const trimmedName = displayName.trim();

		if (!trimmedName) {
			setDisplayName(lastSavedNameRef.current);
			setNameError(translations.nameRequired);
			return;
		}

		if (trimmedName === lastSavedNameRef.current || isSavingName) {
			return;
		}

		setIsSavingName(true);
		setNameError('');

		try {
			const { data } = await updateUserProfileName({ name: trimmedName });
			lastSavedNameRef.current = trimmedName;
			setDisplayName(trimmedName);
			onNameUpdated?.(data);
		} catch {
			setDisplayName(lastSavedNameRef.current);
			setNameError(translations.nameSaveError);
		} finally {
			setIsSavingName(false);
		}
	}, [
		displayName,
		isSavingName,
		onNameUpdated,
		translations.nameRequired,
		translations.nameSaveError,
	]);

	useEffect(() => {
		if (open) {
			wasOpenRef.current = true;
			return undefined;
		}

		if (!wasOpenRef.current) {
			return undefined;
		}

		wasOpenRef.current = false;
		void saveDisplayName();
	}, [open, saveDisplayName]);

	const handleNameKeyDown = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			event.currentTarget.blur();
			void saveDisplayName();
		}
	};

	const handleNameChange = (value) => {
		setDisplayName(value);
		if (nameError) {
			setNameError('');
		}
	};

	return {
		displayName,
		isSavingName,
		nameError,
		handleNameChange,
		handleNameBlur: () => {
			void saveDisplayName();
		},
		handleNameKeyDown,
	};
};
